import { useCallback, useEffect, useState } from 'react';
import { AUDIO_SEC } from '../../../models/assessmentEvaluation';

/**
 * The one recording's playback state for an open assessment. The <audio>
 * element is passed in through `audioRef` (a callback ref), so a body that
 * re-renders underneath an open dialog, Processing turning into Completed,
 * keeps the element and its position. Closing the dialog removes the element,
 * which stops the clip with it.
 */
export function useAssessmentPlayer() {
  const [audio, setAudio] = useState(null);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(AUDIO_SEC);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!audio) return undefined;
    // A file that never loads, or no file at all, can't be played.
    setFailed(!!audio.error || !audio.getAttribute('src'));
    const sync = () => setTime(audio.currentTime || 0);
    const meta = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) setDuration(audio.duration);
      sync();
    };
    const play = () => setPlaying(true);
    const stop = () => setPlaying(false);
    const fail = () => { setFailed(true); setPlaying(false); };
    audio.addEventListener('timeupdate', sync);
    audio.addEventListener('loadedmetadata', meta);
    audio.addEventListener('play', play);
    audio.addEventListener('pause', stop);
    audio.addEventListener('ended', stop);
    audio.addEventListener('error', fail);
    return () => {
      audio.removeEventListener('timeupdate', sync);
      audio.removeEventListener('loadedmetadata', meta);
      audio.removeEventListener('play', play);
      audio.removeEventListener('pause', stop);
      audio.removeEventListener('ended', stop);
      audio.removeEventListener('error', fail);
      audio.pause();
    };
  }, [audio]);

  const toggle = useCallback(() => {
    if (!audio || failed) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    const started = audio.play();
    // A refused autoplay is not an error the person needs to see; a file the
    // browser can't decode is.
    if (started && started.catch) {
      started.catch((err) => { if (err && err.name === 'NotSupportedError') setFailed(true); });
    }
  }, [audio, failed]);

  /** Moves the playhead, clamped to the clip. Works before the file has loaded. */
  const seek = useCallback((sec) => {
    if (!audio) return;
    const next = Math.max(0, Math.min(duration, sec));
    audio.currentTime = next;
    setTime(next);
  }, [audio, duration]);

  return { audioRef: setAudio, time, duration, playing, failed, toggle, seek };
}

/**
 * Brings `el` to the middle of `scroller`, which scrolls in place, so following
 * the clip moves the transcript and nothing else.
 */
export function centreIn(scroller, el) {
  if (!scroller || !el || !scroller.scrollTo) return;
  const box = scroller.getBoundingClientRect();
  const target = el.getBoundingClientRect();
  const delta = (target.top - box.top) - (scroller.clientHeight / 2 - target.height / 2);
  scroller.scrollTo({ top: Math.max(0, scroller.scrollTop + delta), behavior: 'smooth' });
}
